import {type ReactNode, useEffect, useRef, useState} from 'react'
import {Link, NavLink, Outlet} from 'react-router-dom'
import {useTranslation} from 'react-i18next'
import {brand} from '../brand'
import {LivingDataLogo} from '../brand/logo'
import './public-layout.css'

// NB: studio consumes core-front's compiled CSS and does not generate its own
// Tailwind utilities, so only classes core already emits are available here.
// Anything responsive (burger, width alignment) lives in public-layout.css.
const headerPrimaryClass = [
  'inline-flex items-center justify-center rounded-md bg-primary px-3 py-2',
  'text-sm font-medium text-primary-foreground shadow-sm transition-colors hover:bg-primary/90'
].join(' ')

const headerSecondaryClass = [
  'inline-flex items-center justify-center rounded-md border border-input bg-background px-3 py-2',
  'text-sm font-medium shadow-sm transition-colors hover:bg-accent hover:text-accent-foreground'
].join(' ')

const footerLinkClass = 'text-muted-foreground transition-colors hover:text-foreground'
const LIVINGDATA_URL = 'https://livingdata.co'

function PublicNavLink({to, children}: {readonly to: string; readonly children: ReactNode}) {
  return (
    <NavLink
      to={to}
      className={({isActive}) => [
        'rounded-md px-3 py-2 text-sm font-medium transition-colors',
        isActive ? 'bg-accent text-accent-foreground' : 'text-muted-foreground hover:text-foreground'
      ].join(' ')}
    >
      {children}
    </NavLink>
  )
}

function BurgerIcon({isOpen}: {readonly isOpen: boolean}) {
  return isOpen
    ? (
      <svg width='20' height='20' viewBox='0 0 24 24' fill='none' stroke='currentColor' strokeWidth='2' strokeLinecap='round' aria-hidden='true'>
        <path d='M6 6l12 12M18 6 6 18' />
      </svg>
    )
    : (
      <svg width='20' height='20' viewBox='0 0 24 24' fill='none' stroke='currentColor' strokeWidth='2' strokeLinecap='round' aria-hidden='true'>
        <path d='M4 7h16M4 12h16M4 17h16' />
      </svg>
    )
}

function PublicHeader() {
  const {t} = useTranslation()
  const {name} = brand
  const [menuOpen, setMenuOpen] = useState(false)
  const headerRef = useRef<HTMLElement>(null)
  const closeMenu = () => {
    setMenuOpen(false)
  }

  // Quand le menu mobile est ouvert, le refermer sur Échap ou sur un clic en
  // dehors du header.
  useEffect(() => {
    if (!menuOpen) {
      return
    }

    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape') {
        setMenuOpen(false)
      }
    }

    const onPointerDown = (event: PointerEvent) => {
      if (headerRef.current && !headerRef.current.contains(event.target as Node)) {
        setMenuOpen(false)
      }
    }

    document.addEventListener('keydown', onKeyDown)
    document.addEventListener('pointerdown', onPointerDown)
    return () => {
      document.removeEventListener('keydown', onKeyDown)
      document.removeEventListener('pointerdown', onPointerDown)
    }
  }, [menuOpen])

  return (
    <header ref={headerRef} className='pub-header sticky top-0 z-10 border-b border-border bg-background'>
      <div className='pub-header__wrap flex h-16 w-full items-center gap-6'>
        <Link to='/' className='flex items-center gap-2' onClick={closeMenu}>
          <span className='block h-8 w-8'><LivingDataLogo variant='compact' /></span>
          <span className='pub-brand-name text-base font-semibold tracking-tight'>{name}</span>
        </Link>

        <nav className='pub-nav flex items-center gap-1'>
          <PublicNavLink to='/pricing'>{t('public.nav.pricing')}</PublicNavLink>
          <PublicNavLink to='/docs'>{t('public.nav.docs')}</PublicNavLink>
        </nav>

        <div className='pub-actions ml-auto flex items-center gap-2'>
          <Link to='/sign-in' className={headerSecondaryClass}>{t('public.actions.signIn')}</Link>
          <Link to='/sign-up' className={headerPrimaryClass}>{t('public.actions.signUp')}</Link>
        </div>

        <button
          type='button'
          className='pub-burger'
          aria-label={t('public.nav.menu', {defaultValue: 'Menu'})}
          aria-expanded={menuOpen}
          aria-controls='pub-menu'
          onClick={() => {
            setMenuOpen(open => !open)
          }}
        >
          <BurgerIcon isOpen={menuOpen} />
        </button>
      </div>

      {menuOpen && (
        <div id='pub-menu' className='pub-menu'>
          <Link to='/pricing' className='pub-menu__link' onClick={closeMenu}>{t('public.nav.pricing')}</Link>
          <Link to='/docs' className='pub-menu__link' onClick={closeMenu}>{t('public.nav.docs')}</Link>
          <hr className='pub-menu__sep' />
          <Link to='/sign-in' className='pub-menu__link' onClick={closeMenu}>{t('public.actions.signIn')}</Link>
          <Link to='/sign-up' className='pub-menu__link pub-menu__link--primary' onClick={closeMenu}>{t('public.actions.signUp')}</Link>
        </div>
      )}
    </header>
  )
}

function PublicFooter() {
  const {t} = useTranslation()
  const year = new Date().getFullYear()
  return (
    <footer className='pub-footer border-t border-border bg-background'>
      <div className='pub-footer__wrap'>
        <span className='text-sm text-muted-foreground'>© {year} Living Data. {t('public.footer.rights')}</span>
        <nav className='pub-footer__links text-sm'>
          <a href={LIVINGDATA_URL} target='_blank' rel='noreferrer' className={footerLinkClass}>livingdata.co</a>
          <Link to='/legal' className={footerLinkClass}>{t('public.footer.legal')}</Link>
          <Link to='/terms' className={footerLinkClass}>{t('public.footer.terms')}</Link>
          <Link to='/privacy' className={footerLinkClass}>{t('public.footer.privacy')}</Link>
          <Link to='/contact' className={footerLinkClass}>{t('public.footer.contact')}</Link>
        </nav>
      </div>
    </footer>
  )
}

export function PublicLayout({children}: {readonly children?: ReactNode}) {
  // core-front locks the body (overflow:hidden, #root height:100vh), so the
  // public shell owns its own scroll via `.pub-shell` — otherwise a long page
  // like the landing can't scroll. `flex-1` on <main> still centers auth cards.
  return (
    <div className='pub-shell flex flex-col bg-background'>
      <PublicHeader />
      <main className='flex flex-1 flex-col'>
        {children ?? <Outlet />}
      </main>
      <PublicFooter />
    </div>
  )
}
